import { FlashcardsService } from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { HtmlContent } from "@/components/Common/HtmlContent"
import { FlashcardContent } from "@/components/Flashcards/FlashcardContent"
import { FlashcardSaveNoteModal } from "@/components/Flashcards/FlashcardSaveNoteModal"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { formatDateTime } from "@/utils/format-date"
import { Box, Button, HStack, Text, VStack } from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"

export const Route = createFileRoute("/_layout/study/saved/$cardId")({
  component: StudySavedCardDetailPage,
})

function StudySavedCardDetailPage() {
  const { cardId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const breadcrumbs = useRouteBreadcrumbs("study.index", [
    { label: "Saved Flashcards", href: "/study/saved" },
    { label: "Saved Card" },
  ])

  const savedCardQuery = useQuery({
    queryKey: ["flashcards-saved", cardId],
    queryFn: () => FlashcardsService.getSavedFlashcard({ cardId }),
  })

  const deleteMutation = useMutation({
    mutationFn: () => FlashcardsService.deleteSavedFlashcard({ cardId }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["flashcards-saved"] }),
        queryClient.invalidateQueries({
          queryKey: ["flashcards-saved", cardId],
        }),
      ])
      navigate({ to: "/study/saved" })
    },
  })

  if (savedCardQuery.isLoading) {
    return (
      <PageShell title="Saved flashcard" breadcrumbs={breadcrumbs}>
        <PageSkeleton variant="detail" />
      </PageShell>
    )
  }

  if (!savedCardQuery.data) {
    return (
      <PageShell title="Saved flashcard" breadcrumbs={breadcrumbs}>
        <AppEmptyState
          title="Saved flashcard not found"
          description="This card is no longer available in your saved list."
        />
      </PageShell>
    )
  }

  const savedCard = savedCardQuery.data

  return (
    <PageShell
      title="Saved flashcard"
      description={savedCard.deck_name}
      breadcrumbs={breadcrumbs}
      py={{ base: 5, md: 8 }}
      contentGap={5}
    >
      <HStack justify="space-between" align="start" wrap="wrap" gap={3}>
        <Text fontSize="sm" color="fg.muted">
          Updated {formatDateTime(savedCard.updated_at)}
        </Text>
      </HStack>

      <VStack align="stretch" gap={4}>
        <Box
          borderWidth={1}
          borderColor="gray.200"
          _dark={{ borderColor: "gray.700", bg: "gray.900" }}
          borderRadius="lg"
          bg="white"
          p={{ base: 4, md: 5 }}
        >
          <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={3}>
            Deck: {savedCard.deck_name}
          </Text>
          <Text fontSize="xs" fontWeight="bold" mb={2} color="fg.muted">
            Front
          </Text>
          <FlashcardContent content={savedCard.front} />
          <Text fontSize="xs" fontWeight="bold" mt={5} mb={2} color="fg.muted">
            Back
          </Text>
          <FlashcardContent content={savedCard.back} />
        </Box>

        <Box
          borderWidth={1}
          borderColor="purple.300"
          _dark={{ borderColor: "purple.700", bg: "purple.950" }}
          borderRadius="lg"
          bg="purple.subtle"
          p={{ base: 4, md: 5 }}
        >
          <Text fontSize="xs" fontWeight="semibold" mb={3} color="purple.fg">
            Your note
          </Text>
          {savedCard.note_html ? (
            <HtmlContent content={savedCard.note_html} />
          ) : (
            <Text color="fg.muted">No note added yet.</Text>
          )}
        </Box>
      </VStack>

      <HStack justify="space-between" wrap="wrap" gap={3}>
        <Button
          variant="ghost"
          colorPalette="red"
          onClick={() => {
            if (!window.confirm("Remove this saved flashcard?")) {
              return
            }
            deleteMutation.mutate()
          }}
          loading={deleteMutation.isPending}
        >
          Remove saved card
        </Button>
        <FlashcardSaveNoteModal
          card={{
            id: savedCard.card_id,
            deck_id: savedCard.deck_id,
            deck_name: savedCard.deck_name,
            front: savedCard.front,
            back: savedCard.back,
          }}
          triggerLabel="Edit note"
        />
      </HStack>
    </PageShell>
  )
}

export default StudySavedCardDetailPage
